import { AppError } from "../../shared/errors/AppError";
import type { MediaAsset, MediaKind } from "./mediaTypes";

function createId() {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function loadImageMetadata(objectUrl: string) {
  return new Promise<{ width: number; height: number }>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = () => reject(new AppError("DECODE_FAILED", "The image could not be decoded by this browser."));
    image.src = objectUrl;
  });
}

function loadVideoMetadata(objectUrl: string) {
  return new Promise<{ width: number; height: number; duration: number }>((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      resolve({ width: video.videoWidth, height: video.videoHeight, duration });
    };
    video.onerror = () => reject(new AppError("DECODE_FAILED", "The video codec or container could not be decoded by this browser."));
    video.src = objectUrl;
  });
}

export async function readMediaMetadata(file: File, kind: MediaKind, objectUrl: string): Promise<MediaAsset> {
  if (kind === "image") {
    const dimensions = await loadImageMetadata(objectUrl);
    return {
      id: createId(),
      kind,
      name: file.name,
      mimeType: file.type,
      size: file.size,
      objectUrl,
      sourceFile: file,
      dimensions,
    };
  }

  const metadata = await loadVideoMetadata(objectUrl);
  return {
    id: createId(),
    kind,
    name: file.name,
    mimeType: file.type,
    size: file.size,
    objectUrl,
    sourceFile: file,
    dimensions: { width: metadata.width, height: metadata.height },
    duration: metadata.duration,
  };
}
